import { useState } from 'react';

interface Question {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
  solution: string;
  points: number;
}

interface Algebra2TestCardProps {
  state: string;
  stateName: string;
  questions: Question[];
  color: string;
}

export default function Algebra2TestCard({ state, stateName, questions, color }: Algebra2TestCardProps) {
  const [answers, setAnswers] = useState<{ [key: number]: number }>({});
  const [submitted, setSubmitted] = useState(false);
  const [showSolutions, setShowSolutions] = useState<{ [key: number]: boolean }>({});
  const [filter, setFilter] = useState<'all' | 'correct' | 'incorrect'>('all');

  const handleAnswerSelect = (questionId: number, optionIndex: number) => {
    if (!submitted) {
      setAnswers({ ...answers, [questionId]: optionIndex });
    }
  };

  const handleSubmit = () => {
    setSubmitted(true);
  };

  const handleRetake = () => {
    setAnswers({});
    setSubmitted(false);
    setShowSolutions({});
    setFilter('all');
  };

  const toggleSolution = (questionId: number) => {
    setShowSolutions({ ...showSolutions, [questionId]: !showSolutions[questionId] });
  };

  const showAllSolutions = () => {
    const allShown: { [key: number]: boolean } = {};
    questions.forEach(q => {
      allShown[q.id] = true;
    });
    setShowSolutions(allShown);
  };

  const score = submitted
    ? questions.reduce((acc, q) => {
        return acc + (answers[q.id] === q.correctAnswer ? q.points : 0);
      }, 0)
    : 0;

  const totalPoints = questions.reduce((acc, q) => acc + q.points, 0);
  const percentage = submitted ? Math.round((score / totalPoints) * 100) : 0;

  const filteredQuestions = questions.filter(q => {
    if (filter === 'all') return true;
    if (filter === 'correct') return answers[q.id] === q.correctAnswer;
    if (filter === 'incorrect') return answers[q.id] !== q.correctAnswer && answers[q.id] !== undefined;
    return true;
  });

  const allAnswered = questions.every(q => answers[q.id] !== undefined);

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className={`${color} text-white rounded-xl p-8 mb-8`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-3xl font-bold mb-2">{stateName} Algebra 2 Practice Test</h2>
            <p className="text-white/90">30 Questions • {totalPoints} Points Total</p>
          </div>
          <div className="text-right">
            <div className="text-5xl font-bold">{state}</div>
          </div>
        </div>
        {submitted && (
          <div className="bg-white/20 backdrop-blur-sm rounded-lg p-6 mt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-white/80 mb-1">Your Score</div>
                <div className="text-4xl font-bold">{score} / {totalPoints}</div>
                <div className="text-xl mt-1">{percentage}%</div>
              </div>
              <button
                onClick={handleRetake}
                className="bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                Retake Test
              </button>
            </div>
          </div>
        )}
      </div>

      {submitted && (
        <div className="mb-6 flex items-center justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                filter === 'all' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              All Questions
            </button>
            <button
              onClick={() => setFilter('correct')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                filter === 'correct' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Correct
            </button>
            <button
              onClick={() => setFilter('incorrect')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                filter === 'incorrect' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Incorrect
            </button>
          </div>
          <button
            onClick={showAllSolutions}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors whitespace-nowrap"
          >
            Show All Solutions
          </button>
        </div>
      )}

      <div className="space-y-6">
        {filteredQuestions.map((q, index) => {
          const isCorrect = answers[q.id] === q.correctAnswer;
          const isAnswered = answers[q.id] !== undefined;

          return (
            <div
              key={q.id}
              className={`bg-white rounded-xl p-6 shadow-sm border-2 ${
                submitted
                  ? isCorrect
                    ? 'border-green-500'
                    : isAnswered
                    ? 'border-red-500'
                    : 'border-gray-200'
                  : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Question {q.id} ({q.points} {q.points === 1 ? 'point' : 'points'})
                </h3>
                {submitted && (
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${
                      isCorrect ? 'bg-green-100 text-green-800' : isAnswered ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {isCorrect ? '✓ Correct' : isAnswered ? '✗ Incorrect' : 'Not Answered'}
                  </span>
                )}
              </div>

              <p className="text-gray-800 mb-4 whitespace-pre-wrap">{q.question}</p>

              <div className="space-y-2 mb-4">
                {q.options.map((option, optionIndex) => {
                  const isSelected = answers[q.id] === optionIndex;
                  const isCorrectOption = optionIndex === q.correctAnswer;

                  return (
                    <button
                      key={optionIndex}
                      onClick={() => handleAnswerSelect(q.id, optionIndex)}
                      disabled={submitted}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        submitted
                          ? isCorrectOption
                            ? 'border-green-500 bg-green-50'
                            : isSelected
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-200 bg-gray-50'
                          : isSelected
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                      } ${submitted ? 'cursor-default' : 'cursor-pointer'}`}
                    >
                      <div className="flex items-center">
                        <span className="font-semibold mr-3 text-gray-700">
                          {String.fromCharCode(65 + optionIndex)}.
                        </span>
                        <span className="text-gray-800">{option}</span>
                        {submitted && isCorrectOption && (
                          <span className="ml-auto text-green-600 font-bold">✓</span>
                        )}
                        {submitted && isSelected && !isCorrectOption && (
                          <span className="ml-auto text-red-600 font-bold">✗</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {submitted && (
                <button
                  onClick={() => toggleSolution(q.id)}
                  className="text-indigo-600 font-medium hover:text-indigo-700 transition-colors whitespace-nowrap"
                >
                  {showSolutions[q.id] ? '− Hide Solution' : '+ Show Solution'}
                </button>
              )}

              {submitted && showSolutions[q.id] && (
                <div className="mt-4 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <h4 className="font-semibold text-indigo-900 mb-2">Solution:</h4>
                  <p className="text-gray-800 whitespace-pre-wrap">{q.solution}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!submitted && (
        <div className="mt-8 flex justify-center">
          <button
            onClick={handleSubmit}
            disabled={!allAnswered}
            className={`px-8 py-4 rounded-lg font-semibold text-lg transition-all whitespace-nowrap ${
              allAnswered
                ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg hover:shadow-xl'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {allAnswered ? 'Submit Test' : `Answer All Questions (${Object.keys(answers).length}/${questions.length})`}
          </button>
        </div>
      )}
    </div>
  );
}